import { useEffect, useState } from "react";
import {
  DarkVariant,
  getDarkVariant,
  getPreference,
  getResolvedTheme,
  setDarkVariant,
  setPreference,
  subscribe,
  ThemePreference,
  ResolvedTheme,
} from "./theme";

interface UseTheme {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  darkVariant: DarkVariant;
  setPreference: (preference: ThemePreference) => void;
  setDarkVariant: (variant: DarkVariant) => void;
}

// React binding for the theme manager — re-renders on preference / OS change.
// Both `preference` and `resolvedTheme` live in state (as a fresh object per
// notification) so a live OS light<->dark flip while preference === "system"
// still re-renders: `getPreference()` is unchanged ("system") on that event, so
// keying state off preference alone would let React bail and leave
// `resolvedTheme` stale.
export const useTheme = (): UseTheme => {
  const [state, setState] = useState<{
    preference: ThemePreference;
    resolvedTheme: ResolvedTheme;
    darkVariant: DarkVariant;
  }>(() => ({
    preference: getPreference(),
    resolvedTheme: getResolvedTheme(),
    darkVariant: getDarkVariant(),
  }));
  useEffect(
    () =>
      subscribe(() =>
        setState({
          preference: getPreference(),
          resolvedTheme: getResolvedTheme(),
          darkVariant: getDarkVariant(),
        })
      ),
    []
  );
  return {
    preference: state.preference,
    resolvedTheme: state.resolvedTheme,
    darkVariant: state.darkVariant,
    setPreference,
    setDarkVariant,
  };
};
